import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { StudentService } from '../../../../../core/services/student.service';
import { StudentTestService } from '../../../../../core/services/student-test.service';
import { LocalizedLabelService } from '../../../../../core/services/utils/localized-label.service';
import { StudentFeatureService } from '../../../../../core/services/student-feature.service';
import { StudentSubjectProgression } from '../../../../../core/interfaces/student-subject-progression';
import { Test } from '../../../../../core/interfaces/test';
import { ErrorStateComponent } from '../../../../ui/error-state/error-state.component';
import { instantUtc } from '../../../../../core/utils/instant-utc';

/** Une matière dans la colonne de gauche. */
export interface SubjectRow {
  id: number;
  name: string;
  progress: number;
  color: string;

  /**
   * Matière épinglée par l'élève.
   *
   * <p>Le réglage vivait dans un écran séparé, que le hub a remplacé : sans
   * lui ici, épingler une matière serait devenu impossible.</p>
   */
  isFavorite: boolean;
}

/** Un test proposé, avec l'état de sa fenêtre de passation. */
export interface AvailableTest {
  test: Test;
  maxAttempts: number;
  windowState: 'open' | 'scheduled';
}

/**
 * Une session de test de l'élève — rendue ou encore ouverte.
 *
 * <p>Les deux cas se distinguent par le <code>status</code>, et la distinction
 * n'est pas cosmétique : une session jamais rendue n'a ni note ni durée, et la
 * colonne <code>percentage_score</code> vaut <code>0.00</code> par défaut en
 * base. Prise pour une note, cette valeur affichait « 0 % » à un élève qui
 * n'avait pas répondu à une seule question — et la faisait entrer dans sa
 * moyenne.</p>
 */
export interface AttemptRow {
  sessionId: number;
  testId: number;
  testTitle: string;
  testMode: string;
  attemptNumber: number;
  status: string;
  startedAt: string;

  /** Heure de fin de la session, quand le test est chronométré. */
  deadline: string | null;

  percentageScore: number | null;
  timeSpentSeconds: number | null;
  passed: boolean | null;
}

/**
 * Une copie est rendue quand l'élève l'a remise — pas quand il a ouvert le
 * test. Tant qu'elle ne l'est pas, ni la note ni la durée ne veulent dire quoi
 * que ce soit.
 */
export function estRendue(a: AttemptRow): boolean {
  return a.status === 'completed' || a.status === 'submitted';
}

/**
 * L'heure de fin de la session est passée : elle ne se reprend plus.
 *
 * <p>Le serveur la clôt au prochain contact — abandonnée si elle est vide,
 * corrigée si l'élève avait répondu — et ouvre une copie blanche. Lui proposer
 * « استئناف » promettrait de retrouver un test qu'il ne retrouvera pas.</p>
 */
export function estPerimee(a: AttemptRow): boolean {
  return a.deadline != null && instantUtc(a.deadline) < Date.now();
}

// Déplacée dans core/utils : la passation lit aussi les échéances de l'API.
// Réexportée ici pour les appelants existants.
export { instantUtc };

/**
 * Espace tests de l'élève, en une seule page.
 *
 * <p>Remplace la navigation à trois niveaux (grille de matières → espace d'une
 * matière → trois onglets, le tout dédoublé par deux entrées de menu). L'élève
 * choisit une matière à gauche et voit à droite, dans l'ordre de ce qui compte :
 * son diagnostic ou son rapport, l'entraînement, les devoirs, ses copies.</p>
 *
 * <p>Attention au nommage des modes, contre-intuitif mais établi : en base, le
 * test diagnostique porte <code>evaluation</code> et le devoir de contrôle
 * <code>exam</code>.</p>
 */
@Component({
  selector: 'app-tests-hub',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, ErrorStateComponent],
  templateUrl: './tests-hub.component.html',
  styleUrl: './tests-hub.component.css',
})
export class TestsHubComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly students = inject(StudentService);
  private readonly studentTests = inject(StudentTestService);
  private readonly labels = inject(LocalizedLabelService);
  private readonly features = inject(StudentFeatureService);

  /**
   * Ce que le pack ouvre ici, un type de test à la fois (SKILL2ERA-137). Faux
   * tant que la réponse n'est pas là : la page ne montre pas un instant ce que
   * le pack n'inclut pas, et ne lit pas un mode que le serveur refuserait — un
   * 403 en lecture renverrait l'élève vers « accès refusé ».
   */
  readonly showsDiagnostic = signal(false);
  readonly showsPractice = signal(false);
  readonly showsExams = signal(false);

  /** Teintes de la colonne : la couleur distingue, à défaut d'icône. */
  private static readonly PALETTE = [
    'var(--cpurple1)', 'var(--cblue5)', 'var(--cgreen5)',
    'var(--cyellow4)', 'var(--cred5)', 'var(--cpurple0)',
  ];

  readonly subjects = signal<SubjectRow[]>([]);
  readonly selectedId = signal<number | null>(null);

  readonly diagnostic = signal<AvailableTest | null>(null);
  readonly exams = signal<AvailableTest[]>([]);
  readonly attempts = signal<AttemptRow[]>([]);

  readonly loadingSubjects = signal(true);
  readonly loadingDetail = signal(false);
  readonly errored = signal(false);

  readonly selected = computed(() =>
    this.subjects().find((s) => s.id === this.selectedId()) ?? null,
  );

  /** Les copies effectivement rendues — celles qui portent une note. */
  readonly rendues = computed(() => this.attempts().filter(estRendue));

  /**
   * La session de diagnostic laissée ouverte, s'il y en a une.
   *
   * <p>C'est le cas de l'élève interrompu : onglet fermé, réseau coupé. Il ne
   * doit lire ni « copie rendue » ni note, mais se voir proposer de reprendre
   * là où il s'était arrêté.</p>
   */
  readonly diagnosticEnCours = computed(
    () =>
      this.attempts().find(
        (a) =>
          a.testMode === 'evaluation' &&
          a.status === 'in_progress' &&
          !estPerimee(a),
      ) ?? null,
  );

  /**
   * Copie la plus récente du diagnostic. C'est elle qui décide de l'action
   * proposée : passer le test, ou consulter le rapport.
   *
   * Retrouvée par le **mode du test**, et non par l'identifiant du diagnostic
   * encore ouvert. La fenêtre de passation finit toujours par se fermer : en
   * s'appuyant sur elle, la copie déjà rendue disparaissait de l'écran le jour
   * où le test cessait d'être disponible — et le rapport avec, alors que c'est
   * précisément à ce moment-là qu'on vient le consulter.
   */
  readonly diagnosticAttempt = computed(() => {
    const parMode = this.rendues().filter((a) => a.testMode === 'evaluation');
    if (parMode.length === 0) return null;

    /* La plus récente fait foi : un diagnostic se repasse, et c'est la dernière
       tentative qui décrit où en est l'élève. */
    return parMode.reduce((recente, a) =>
      (a.startedAt ?? '') > (recente.startedAt ?? '') ? a : recente,
    );
  });

  /**
   * Intitulé affiché : celui du test ouvert, à défaut celui de la copie.
   *
   * Vide quand la matière n'a ni session ouverte ni copie rendue — le bandeau
   * reste affiché et annonce alors que le diagnostic n'est pas encore ouvert.
   */
  readonly diagnosticTitle = computed(
    () => this.diagnostic()?.test.title ?? this.diagnosticAttempt()?.testTitle ?? '',
  );

  /** Compteurs de l'en-tête, calculés sur ce qui est réellement chargé. */
  readonly stats = computed(() => {
    // Les copies rendues seulement : une session ouverte n'est pas une copie,
    // et sa note par défaut fausserait la moyenne affichée.
    const done = this.rendues();
    const scored = done.filter((a) => a.percentageScore != null).slice(0, 5);
    const average = scored.length
      ? Math.round(scored.reduce((s, a) => s + (a.percentageScore ?? 0), 0) / scored.length)
      : null;

    return {
      available: this.exams().length + (this.diagnostic() ? 1 : 0),
      done: done.length,
      average,
      todo: this.exams().filter((e) => e.windowState === 'open').length,
    };
  });

  ngOnInit(): void {
    // Les fonctionnalités d'abord : le détail d'une matière n'interroge que les
    // types de test que le pack ouvre.
    void this.features.load().then(() => {
      this.showsDiagnostic.set(this.features.allows('diagnostic'));
      this.showsPractice.set(this.features.allows('practice'));
      this.showsExams.set(this.features.allows('exams'));
      this.loadSubjects();
    });
  }

  select(id: number): void {
    if (this.selectedId() === id) return;
    this.selectedId.set(id);
    // La matière vit dans l'URL : l'élève peut partager ou recharger sa page
    // sans repartir de la première matière.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { subject: id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.loadDetail(id);
  }

  reload(): void {
    this.loadSubjects();
  }

  /** Segments vers la composition d'un test d'entraînement. */
  composeLink(): unknown[] {
    return ['/student/tests/generate'];
  }

  composeParams(): Record<string, unknown> {
    return { subject: this.selectedId() };
  }

  /** Durée lisible ; tiret si le serveur ne l'a pas renvoyée. */
  duration(seconds: number | null): string {
    if (seconds == null || seconds <= 0) return '—';
    const m = Math.round(seconds / 60);
    return `${m} min`;
  }

  private loadSubjects(): void {
    this.loadingSubjects.set(true);
    this.errored.set(false);

    this.students
      .getMySubjectsProgression()
      .pipe(
        catchError(() => of(null)),
        finalize(() => this.loadingSubjects.set(false)),
      )
      .subscribe((res) => {
        if (!res) {
          // Une liste vide reste vide : aucune matière de démonstration.
          this.subjects.set([]);
          this.errored.set(true);
          return;
        }

        const rows = (res.data ?? []).map((p, i) => this.toRow(p, i));
        this.subjects.set(rows);

        // Matière de l'URL si elle est valide, sinon la première : l'écran ne
        // s'ouvre jamais sur un panneau vide alors que des matières existent.
        const asked = Number(this.route.snapshot.queryParamMap.get('subject'));
        const initial =
          rows.find((r) => r.id === asked)?.id ?? rows[0]?.id ?? null;

        this.selectedId.set(initial);
        if (initial) this.loadDetail(initial);
      });
  }

  private toRow(p: StudentSubjectProgression, index: number): SubjectRow {
    return {
      id: p.subjectId,
      name: this.labels.pickPrefixed(p as unknown as Record<string, unknown>, 'subjectName'),
      progress: Math.max(0, Math.min(100, Math.round(p.progressionRate ?? 0))),
      color: TestsHubComponent.PALETTE[index % TestsHubComponent.PALETTE.length],
      isFavorite: !!p.isFavorite,
    };
  }

  /**
   * Épingle ou décroche une matière.
   *
   * <p>L'état bascule d'abord à l'écran, puis la requête part : attendre la
   * réponse pour peindre l'étoile rendrait le geste mou sur une connexion
   * lente. Un échec la remet en place — mieux vaut un retour en arrière visible
   * qu'un état affiché qui ne correspond à rien.</p>
   *
   * <p>Le clic ne sélectionne pas la matière : l'étoile arrête la propagation,
   * sans quoi épingler ferait aussi changer de panneau.</p>
   */
  toggleFavorite(row: SubjectRow, event: Event): void {
    event.stopPropagation();

    const next = !row.isFavorite;
    this.setFavoriteLocally(row.id, next);

    this.students.setSubjectFavorite(row.id, next).subscribe({
      next: (res) => this.setFavoriteLocally(row.id, res?.data?.isFavorite ?? next),
      error: () => this.setFavoriteLocally(row.id, !next),
    });
  }

  private setFavoriteLocally(subjectId: number, isFavorite: boolean): void {
    this.subjects.update((list) =>
      list.map((s) => (s.id === subjectId ? { ...s, isFavorite } : s)),
    );
  }

  private loadDetail(subjectId: number): void {
    this.loadingDetail.set(true);

    forkJoin({
      // `evaluation` = test diagnostique, `exam` = devoir de contrôle. Un mode
      // que le pack n'ouvre pas n'est pas demandé.
      diagnostic: this.showsDiagnostic()
        ? this.studentTests.getAvailableByModeAndSubject('evaluation', subjectId).pipe(catchError(() => of(null)))
        : of(null),
      exams: this.showsExams()
        ? this.studentTests.getAvailableByModeAndSubject('exam', subjectId).pipe(catchError(() => of(null)))
        : of(null),
      sessions: this.studentTests
        .getMySessionsBySubject(subjectId)
        .pipe(catchError(() => of(null))),
    })
      .pipe(finalize(() => this.loadingDetail.set(false)))
      .subscribe(({ diagnostic, exams, sessions }) => {
        const diag = (diagnostic?.data ?? []).map((t) => this.toAvailable(t));
        this.diagnostic.set(diag[0] ?? null);
        this.exams.set((exams?.data ?? []).map((t) => this.toAvailable(t)));

        this.attempts.set(
          (sessions?.data ?? []).map((s: any) => ({
            sessionId: s.id,
            testId: s.testId ?? s.test?.id ?? 0,
            testTitle: s.test?.title ?? '—',
            testMode: s.test?.testMode ?? '',
            attemptNumber: s.attemptNumber ?? 0,
            status: s.status ?? '',
            startedAt: s.startedAt ?? s.createdAt ?? '',
            deadline: s.deadline ?? null,
            percentageScore: s.percentageScore ?? null,
            timeSpentSeconds: s.timeSpentSeconds ?? null,
            passed: s.passed ?? null,
          })),
        );
      });
  }

  /** Fenêtre de passation : « publié » ne veut pas dire « ouvert ». */
  private toAvailable(test: Test): AvailableTest {
    const start = test.startDate ? new Date(test.startDate).getTime() : null;
    return {
      test,
      maxAttempts: test.maxAttempts ?? 1,
      windowState: start && start > Date.now() ? 'scheduled' : 'open',
    };
  }
}
